import React from 'react';
import { SetupState, ThemePreset } from '../types';
import { themePresets } from '../data/themes';
import { QrCode, AlertCircle, Sparkles, Receipt, Info } from 'lucide-react';
import generatePayload from 'promptpay-qr';
import QRCode from 'react-qr-code';

interface BillPreviewProps {
  state: SetupState;
}

export const BillPreview: React.FC<BillPreviewProps> = ({ state }) => {
  const activeTheme = themePresets.find(theme => theme.id === state.themeId) || themePresets[0];
  const themeColor = activeTheme.hex || '#FF6B1A';

  const previewRoom = state.rooms.length > 0 ? state.rooms[0].roomName : 'A101';
  const dormName = state.dormName || 'หอพักของคุณ';

  const oldElec = 1200;
  const newElec = 1325;
  const elecUsage = newElec - oldElec;
  const elecCost = state.elecRateType === 'per_room'
    ? (state.elecRate || 0)
    : elecUsage * (state.elecRate || 0);

  const waterRate = Math.max(0, parseFloat(state.waterRatePrice) || 0);
  const waterCost = state.waterRateType === 'per_person' ? (waterRate * 2) : waterRate;

  let commonCost = 0;
  if (state.hasCommonFee) {
    const feePrice = Math.max(0, parseFloat(state.commonFeePrice) || 0);
    commonCost = state.commonFeeType === 'per_person' ? (feePrice * 2) : feePrice;
  }

  let wifiCost = 0;
  if (state.hasWifiFee) {
    const feePrice = Math.max(0, parseFloat(state.wifiFeePrice) || 0);
    wifiCost = state.wifiFeeType === 'per_person' ? (feePrice * 2) : feePrice;
  }

  const subtotal = 1.00;

  const formatMoney = (n: number) => {
    return n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPromptPay = (num: string) => {
    if (!num) return '-';
    const cleanNum = num.replace(/\D/g, '');
    if (cleanNum.length === 10) {
      return `${cleanNum.slice(0,3)}-${cleanNum.slice(3,6)}-${cleanNum.slice(6)}`;
    } else if (cleanNum.length === 13) {
      return `${cleanNum.slice(0,1)}-${cleanNum.slice(1,5)}-${cleanNum.slice(5,10)}-${cleanNum.slice(10,12)}-${cleanNum.slice(12)}`;
    }
    return num;
  };

  const formatAccountName = (name: string) => {
    if (!name) return '';
    return name.trim();
  };

  const getQrPayload = () => {
    if (!state.promptPayNumber) return 'incorrect';
    const cleanNum = state.promptPayNumber.replace(/\D/g, '');
    if (cleanNum.length === 10 || cleanNum.length === 13 || cleanNum.length === 15) {
      try {
        return generatePayload(cleanNum, {}); // open amount / 0 baht
      } catch (e) {
        return 'incorrect';
      }
    }
    return 'incorrect';
  };
  
  const qrPayload = getQrPayload();

  const logoScale = Math.min(3.0, Math.max(0.5, state.logoScale || 1.0));
  const logoPositionX = state.logoPositionX || 0;
  const logoPositionY = state.logoPositionY || 0;
  const initialLetter = state.dormName ? state.dormName.charAt(0).toUpperCase() : 'ห';

  // CSS Styles ported from Admin-v2
  const adminV2Styles = `
    .slate-bill-card {
      position: relative;
      width: 450px !important;
      min-width: 450px !important;
      max-width: 450px !important;
      height: 800px !important;
      min-height: 800px !important;
      max-height: 800px !important;
      margin: 0 auto;
      padding: 0;
      box-sizing: border-box;
      overflow: hidden;
      border-radius: 24px;
      background: #ffffff;
      border: 1px solid #f3f4f6;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      font-family: 'Sarabun', sans-serif;
      color: #1e293b;
      transform: scale(0.85);
      transform-origin: top center;
    }
    .slate-bill-card * { box-sizing: border-box; }
    .slate-bill-inner {
      position: relative;
      width: 100%;
      height: 100%;
      padding: 16px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    .slate-bill-upper {
      width: 100%;
      height: 435px;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      z-index: 10;
      position: relative;
      overflow: visible;
    }
    .slate-bill-brand {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      z-index: 10;
    }
    .slate-bill-logo-wrap {
      width: 64px;
      height: 64px;
      border-radius: 999px;
      border: 2px solid #0f172a;
      overflow: hidden;
      background: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      font-size: 20px;
      clip-path: circle(50% at 50% 50%);
      -webkit-mask-image: radial-gradient(circle, #000 99%, transparent 100%);
      mask-image: radial-gradient(circle, #000 99%, transparent 100%);
      isolation: isolate;
      position: relative;
    }
    .slate-bill-logo-wrap img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center center;
      display: block;
    }
    .slate-bill-dorm-name {
      margin: 0;
      font-size: 18px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: -0.025em;
      line-height: 1;
    }
    .slate-bill-semester {
      margin: 4px 0 0;
      font-size: 10px;
      color: #9ca3af;
      text-transform: uppercase;
      font-weight: 600;
      letter-spacing: 0.05em;
    }
    .slate-bill-room-pill {
      margin-top: 4px;
      padding: 2px 20px;
      border-radius: 999px;
      color: #fff;
      font-size: 16px;
      font-weight: 900;
      letter-spacing: 0.1em;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
    .slate-bill-dash {
      margin: 6px 0 4px;
      border-top: 1px dashed #e2e8f0;
      width: 100%;
    }
    .slate-bill-meter-card {
      background: #f4f7fa;
      border-radius: 16px;
      padding: 10px;
      border: 1px solid #f1f5f9;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
    .slate-bill-meter-cell {
      flex: 1;
      text-align: center;
    }
    .slate-bill-meter-cell p:first-child {
      font-size: 9.5px;
      font-weight: 700;
      color: #64748b;
      line-height: 1;
      margin: 0;
    }
    .slate-bill-meter-cell p:last-child {
      font-size: 13px;
      font-weight: 900;
      color: #1e293b;
      margin: 4px 0 0;
    }
    .slate-bill-meter-divider {
      width: 1.2px;
      height: 32px;
      background: #e2e8f0;
    }
    .slate-bill-items-card {
      backdrop-filter: blur(1px);
      padding: 8px;
      border-radius: 12px;
      border: 1px solid rgba(243, 244, 246, 0.8);
      background: rgba(255, 255, 255, 0.4);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-top: 15px;
    }
    .slate-bill-items-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 4px;
      border-bottom: 1px solid #f3f4f6;
      font-size: 9px;
      color: #9ca3af;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .slate-bill-line {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 2px 0;
      font-size: 12px;
    }
    .slate-bill-line-title {
      font-weight: 500;
      color: #0f172a;
    }
    .slate-bill-line-sub {
      font-size: 9px;
      color: #9ca3af;
      display: block;
    }
    .slate-bill-line-amount {
      font-weight: 800;
      color: #0f172a;
    }
    .slate-bill-cut-line {
      position: absolute;
      top: 454px;
      left: 16px;
      right: 16px;
      z-index: 10;
    }
    .slate-bill-cut-line hr {
      border: none;
      border-top: 2px dashed #e2e8f0;
      opacity: 0.6;
      margin: 0;
    }
    .slate-bill-lower {
      position: absolute;
      bottom: 16px;
      left: 16px;
      right: 16px;
      height: 310px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      z-index: 10;
    }
    .slate-bill-pp-header {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 48px;
      width: 148px;
      overflow: hidden;
      border-radius: 12px;
      background: #1a3763;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
    .slate-bill-pp-header img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transform: scale(1.05);
    }
    .slate-bill-qr-wrap {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }
    .slate-bill-qr-box {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      background: #fff;
      border-radius: 16px;
      width: 140px;
      height: 140px;
      z-index: 10;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }
    .slate-bill-qr-fallback {
      color: #e74c3c;
      font-size: 0.8rem;
      text-align: center;
      margin-top: 10px;
      font-weight: 500;
      padding: 20px 0;
      border: 1px dashed #e74c3c;
      border-radius: 8px;
      width: 100%;
    }
    .slate-bill-pp-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #f1f5f9;
      padding: 2px 12px;
      border-radius: 999px;
    }
    .slate-bill-pp-dot {
      width: 8px;
      height: 8px;
      border-radius: 999px;
      background: #10b981;
      animation: slate-bill-pulse 1.5s ease-in-out infinite;
    }
    @keyframes slate-bill-pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    .slate-bill-pp-id {
      font-size: 11px;
      font-weight: 900;
      color: #1e293b;
      letter-spacing: 0.05em;
      font-family: ui-monospace, monospace;
    }
    .slate-bill-receiver {
      font-size: 11px;
      font-weight: 800;
      color: #475569;
      text-align: center;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 0;
    }
    .slate-bill-total-bar {
      width: 100%;
      border-radius: 16px;
      background: #0f172a;
      color: #fff;
      padding: 6px;
      text-align: center;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    .slate-bill-total-label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      opacity: 0.85;
      font-weight: 700;
    }
    .slate-bill-total-amount {
      font-size: 18px;
      font-weight: 900;
      letter-spacing: -0.025em;
      line-height: 1;
      margin-top: 2px;
      font-family: ui-monospace, monospace;
    }
    #bill-preview-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
    }
    .slate-preview-container {
      width: 100%;
      display: flex;
      justify-content: center;
      overflow: hidden;
      height: 700px;
    }
  `;

  return (
    <div id="bill-preview-card" className="w-full">
      <style dangerouslySetInnerHTML={{ __html: adminV2Styles }} />
      
      <div className="flex items-center justify-between mb-3 px-1 w-[400px] max-w-full">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 font-sans">
            ตัวอย่างบิล (Live Preview)
          </span>
        </div>
        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 animate-pulse">
          <Sparkles className="w-3.5 h-3.5" />
          <span>จำลองสีบิลทันที</span>
        </div>
      </div>

      <div className="slate-preview-container">
        <div className="slate-bill-card">
          <div className="slate-bill-inner">
            <div className="slate-bill-upper">
              <div className="slate-bill-brand">
                <div className="slate-bill-logo-wrap" style={{ background: themeColor, borderColor: themeColor }}>
                  {state.logoUrl ? (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      clipPath: 'circle(50% at 50% 50%)',
                      transformOrigin: 'center center',
                      transform: `translate(${logoPositionX * 100}%, ${logoPositionY * 100}%) scale(${logoScale})`
                    }}>
                      <img 
                        src={state.logoUrl} 
                        alt="Logo" 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center center',
                          display: 'block'
                        }} 
                        crossOrigin="anonymous" 
                        referrerPolicy="no-referrer" 
                      />
                    </div>
                  ) : (
                    <div className="text-white font-bold" style={{ fontSize: '28px' }}>{initialLetter}</div>
                  )}
                </div>
                <h1 className="slate-bill-dorm-name">{dormName}</h1>
                <p className="slate-bill-semester">ประจำเดือน พฤษภาคม 2569</p>
                <div className="slate-bill-room-pill" style={{ background: themeColor }}>ห้อง {previewRoom}</div>
              </div>
              
              <div className="slate-bill-dash"></div>
              
              <div className="slate-bill-meter-card">
                <div className="slate-bill-meter-cell">
                  <p>เลขมิเตอร์ไฟก่อนหน้า</p>
                  <p>{oldElec.toLocaleString('th-TH')} หน่วย</p>
                </div>
                <div className="slate-bill-meter-divider"></div>
                <div className="slate-bill-meter-cell">
                  <p>เลขมิเตอร์ไฟปัจจุบัน</p>
                  <p>{newElec.toLocaleString('th-TH')} หน่วย</p>
                </div>
              </div>

              <div className="slate-bill-items-card">
                <div className="slate-bill-items-head">
                  <span>รายการค่าใช้จ่าย</span>
                  <span>จำนวนเงิน</span>
                </div>
                
                {/* Electricity */}
                <div className="slate-bill-line">
                  <div>
                    <span className="slate-bill-line-title">
                      {state.elecRateType === 'per_room' ? 'ค่าไฟฟ้า (เหมาจ่ายรายห้อง)' : 'ค่าไฟฟ้า (มิเตอร์)'}
                    </span>
                    <span className="slate-bill-line-sub">
                      {state.elecRateType === 'per_room'
                        ? `เหมาจ่าย ${state.elecRate} บาท/ห้อง`
                        : `หน่วยละ ${state.elecRate} บาท x ${elecUsage} หน่วย`}
                    </span>
                  </div>
                  <span className="slate-bill-line-amount">{formatMoney(elecCost)} บาท</span>
                </div>

                {/* Water */}
                <div className="slate-bill-line">
                  <div>
                    <span className="slate-bill-line-title">
                      {state.waterRateType === 'per_unit'
                        ? 'ค่าน้ำ (มิเตอร์)'
                        : state.waterRateType === 'per_person'
                          ? 'ค่าน้ำ (เหมาจ่ายรายคน)'
                          : 'ค่าน้ำ (เหมาจ่ายรายห้อง)'}
                    </span>
                    <span className="slate-bill-line-sub">
                      {state.waterRateType === 'per_unit'
                        ? `หน่วยละ ${waterRate} บาท x 1 หน่วย`
                        : state.waterRateType === 'per_person'
                          ? `เหมาจ่าย ${waterRate} บาท/คน x 2 คน`
                          : `เหมาจ่าย ${waterRate} บาท/ห้อง`}
                    </span>
                  </div>
                  <span className="slate-bill-line-amount">{formatMoney(waterCost)} บาท</span>
                </div>

                {/* Common Fee */}
                {state.hasCommonFee && (
                  <div className="slate-bill-line">
                    <div>
                      <span className="slate-bill-line-title">
                        ค่าส่วนกลาง ({state.commonFeeType === 'per_person' ? 'คิดตามรายคน' : 'คิดตามรายห้อง'})
                      </span>
                      <span className="slate-bill-line-sub">
                        {state.commonFeeType === 'per_person' 
                          ? `${parseFloat(state.commonFeePrice) || 0} บาท/คน x 2 คน` 
                          : `${parseFloat(state.commonFeePrice) || 0} บาท/ห้อง`}
                      </span>
                    </div>
                    <span className="slate-bill-line-amount">{formatMoney(commonCost)} บาท</span>
                  </div>
                )}

                {/* Wifi Fee */}
                {state.hasWifiFee && (
                  <div className="slate-bill-line">
                    <div>
                      <span className="slate-bill-line-title">
                        บริการ Wi-Fi ({state.wifiFeeType === 'per_person' ? 'ต่อผู้ใช้งาน' : 'เหมาจ่ายต่อห้อง'})
                      </span>
                      <span className="slate-bill-line-sub">
                        {state.wifiFeeType === 'per_person' 
                          ? `${parseFloat(state.wifiFeePrice) || 0} บาท/คน x 2 คน` 
                          : `${parseFloat(state.wifiFeePrice) || 0} บาท/ห้อง`}
                      </span>
                    </div>
                    <span className="slate-bill-line-amount">{formatMoney(wifiCost)} บาท</span>
                  </div>
                )}
              </div>
            </div>

            <div className="slate-bill-cut-line"><hr /></div>

            <div className="slate-bill-lower">
              <div className="slate-bill-pp-header">
                <img src="https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/hIYsuyuFZ03CCqzcGNVu/pub/XhCq7NTuKedWecWRDXJb.png" alt="PromptPay" crossOrigin="anonymous" referrerPolicy="no-referrer" />
              </div>
              
              <div className="slate-bill-qr-wrap">
                <div className="slate-bill-qr-box">
                  <QRCode value={qrPayload} size={132} level="M" style={{ width: '100%', height: '100%' }} />
                </div>
              </div>

              <div className="slate-bill-pp-chip">
                <span className="slate-bill-pp-dot"></span>
                <span className="slate-bill-pp-id">พร้อมเพย์: {state.promptPayNumber ? formatPromptPay(state.promptPayNumber) : '-'}</span>
              </div>
              <p className="slate-bill-receiver">ชื่อบัญชีผู้รับ: {!state.promptPayNumber ? '-' : (state.accountName ? formatAccountName(state.accountName) : 'ยังไม่กำหนด')}</p>

              <div className="slate-bill-total-bar" style={{ background: themeColor }}>
                <div className="slate-bill-total-label">สแกนจ่ายเพื่อชำระเงิน</div>
                <div className="slate-bill-total-amount">{formatMoney(subtotal)} บาท</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <p className="text-center font-sans text-[10px] text-slate-400 tracking-wider mt-2 uppercase font-medium">
        * เอกสารจำลองระบบตั้งค่าเริ่มต้น *
      </p>
    </div>
  );
};
